import { useDynamicIslandContext } from '@/features/dynamic-island/dynamic-island-context'
import { DynamicIslandHelpers, DynamicIslandItem } from '@/features/dynamic-island/dynamic-island-item'
import { useHasOtherActiveDynamicIsland } from '@/features/dynamic-island/dynamic-island-registry'
import { run } from 'shared'
import { cn } from '@/utils/styles/cn'
import { AnimatePresence, motion, useWillChange } from 'framer-motion'
import { Fragment, memo, useMemo } from 'react'

export const DynamicIsland = memo(({ yieldsToOthers }: { yieldsToOthers?: boolean }) => {
    const willChange = useWillChange()

    const { states, contextId } = useDynamicIslandContext()
    const hasOtherActive = useHasOtherActiveDynamicIsland(contextId)
    const shouldYield = yieldsToOthers && hasOtherActive

    const exists = useMemo(() => !!states.length && !shouldYield, [states, shouldYield])

    const openItems = useMemo(
        () =>
            states
                .map((state) => DynamicIslandHelpers.mapStateToStep(state.state, state.stateMap))
                .filter((step) => step === 'content' || step === 'closing' || step === 'final-remark'),
        [states]
    )

    const openCount = openItems.length
    const isOpen = openCount > 0

    return (
        <AnimatePresence>
            {exists && (
                <motion.div
                    style={{ willChange }}
                    initial={{ opacity: 0, scale: 0, y: 10, x: '-50%' }}
                    {...run(() => {
                        switch (isOpen) {
                            case false:
                                return {
                                    animate: {
                                        opacity: 1,
                                        scale: 1,
                                        y: 0,
                                        x: '-50%',
                                    },
                                }

                            case true:
                                return {
                                    animate: {
                                        opacity: 1,
                                        scale: 1,
                                        y: 0,
                                        x: '-50%',
                                    },
                                }
                        }
                    })}
                    exit={{
                        opacity: 0,
                        scale: 0,
                        x: '-50%',
                        transition: {
                            opacity: { duration: 0.25, ease: 'easeOut' },
                            scale: { duration: 0.7, type: 'spring', bounce: 0.03 },
                        },
                    }}
                    key={'dynamic-island'}
                    className={cn(
                        'select-none bg-black fixed top-6 left-1/2 z-100 px-5 py-3 text-[14px] font-medium text-white rounded-full flex items-center justify-center shadow-custom h-11 min-w-fit'
                    )}
                    id="dynamic-island"
                >
                    {states.map((state, index) => {
                        return (
                            <Fragment key={'dynamic-islands-mapped' + state.id}>
                                <DynamicIslandItem index={index} openCount={openCount} {...state} />
                            </Fragment>
                        )
                    })}
                </motion.div>
            )}
        </AnimatePresence>
    )
})

DynamicIsland.displayName = 'DynamicIsland'
